//Built-in libraries

import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
//Third party libraries
import { useDispatch } from 'react-redux';
import usePlacesAutocomplete, { getDetails, getGeocode, getLatLng } from 'use-places-autocomplete';
import Input from '@/components/UI/Form/Input';
//Assets and Miscellaneous
import { States } from '@/constants';
import { constructionType, projectType } from '@/constants/Project';
//Services
import { setProjectBaselineInfo } from '@/store/Projects/Project/BaselineProjectInfo';
// import { getAddressDecode } from "../../../../../api/misc";
import { notifyUser } from '@/store/Toast';
import type {
  ReactSelectOption,
  State,
  // Subscription,
} from '@/types/Constants.ts';
import { fetchNeighborhoods } from '@/utils/location';
import Button from '../../../../UI/Button';
import { HookForm } from '../../../../UI/HookForm/Form';
import { HookInput } from '../../../../UI/HookForm/Form/Input';
//Custom components
import HookMaskedInput from '../../../../UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '../../../../UI/HookForm/Form/ReactSelect';
import ProjectSocialGoalsHookFieldArray from '../../ProjectSocialGoalsHookFieldArray';
import styles from '../Form.module.css';
import { projectTypeWithAvgSalesPrice, projectTypeWithUnits } from './constants';

//Component Begin
export const BasicProjectInfoForm = ({
  handleNext,
  authorizedProjectLeadUsers,
}: {
  handleNext: (type: string) => void;
  authorizedProjectLeadUsers: ReactSelectOption[];
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const projectTypeValue = useWatch({ name: 'projectType' });
  const cityValue = useWatch({ name: 'city' });
  const stateValue = useWatch({ name: 'state' });
  const {
    setValue: setFormValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const neighborhood = fetchNeighborhoods({
    state: stateValue?.abbreviation ?? '',
    city: cityValue ?? '',
  });

  const neighborhoodOptions = (neighborhood ?? [])
    .filter((n) => n?.trim())
    .map((n) => ({
      label: n,
      value: n,
    }));

  const previousCityState = useRef({ city: cityValue, state: stateValue?.abbreviation });

  useEffect(() => {
    const previous = previousCityState.current;
    if (previous.city !== cityValue || previous.state !== stateValue?.abbreviation) {
      setFormValue('neighborhood', null);
      previousCityState.current = { city: cityValue, state: stateValue?.abbreviation };
    }
  }, [cityValue, stateValue?.abbreviation, setFormValue]);

  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    callbackName: 'initMap',
    requestOptions: { componentRestrictions: { country: 'us' } },
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (ref.current?.contains(target)) {
        return;
      }
      if (
        target.closest(
          '.react-select__menu, .react-select__menu-portal, .react-select__control, .react-select__option, .react-select__input-container'
        )
      ) {
        return;
      }
      clearSuggestions();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clearSuggestions]);

  useEffect(() => {
    setValue(getValues('address'));
  }, [getValues, setValue]);

  const handleAddressSelection = (address: any) => {
    const { main_text } = address.structured_formatting;

    getDetails({
      placeId: address.place_id,
      fields: ['address_components'],
    }).then((details) => {
      const { address_components } = details as {
        address_components: {
          long_name: string;
          short_name: string;
          types: any[];
        }[];
        geometry: any;
      };

      const city = address_components.find((ac) => ac.types.includes('locality'));

      const state = address_components.find((ac) => ac.types.includes('administrative_area_level_1'));

      const neighborhood = address_components.find((ac) => ac.types.includes('administrative_area_level_3'));
      const zipCode = address_components.find((ac) => ac.types.includes('postal_code'));

      setFormValue('address', main_text);
      setValue(main_text, false);
      setFormValue('city', city?.long_name);
      setFormValue('state', {
        name: state?.long_name,
        abbreviation: state?.short_name,
      });
      setFormValue('zipCode', zipCode?.long_name);
      setFormValue(
        'neighborhood',
        neighborhood
          ? {
              label: neighborhood?.long_name,
              value: neighborhood?.long_name,
            }
          : undefined
      );
    });

    clearSuggestions();
  };

  const onSubmit = async (formData: any) => {
    const address = `${formData.address}, ${formData.city}, ${formData.state.name}, ${formData.zipCode}`;

    getGeocode({ address })
      .then((res: any) => {
        const { lat, lng } = getLatLng(res[0]);

        const projectLocation = {
          address1: formData.address,
          city: formData.city,
          state: formData.state.name,
          postalCode: formData.zipCode,
          country: 'United States of America',
          lat: lat,
          lng: lng,
          placeId: res[0].place_id,
        };

        const transformedData = {
          ...formData,
          units: projectTypeWithUnits.includes(formData.projectType.value) ? formData.units : null,
          avgSalesPrice: projectTypeWithAvgSalesPrice.includes(formData.projectType.value) ? formData.avgSalesPrice : null,
          location: projectLocation,
        };

        dispatch(setProjectBaselineInfo(transformedData));
        handleNext('FORWARD');
      })
      .catch((_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Unable to resolve entered address',
            toastType: 'failed',
          })
        );
      });
  };

  return (
    <HookForm onSubmit={onSubmit}>
      <section>
        <h2 className={styles['form-title']}>Baseline Project Information</h2>
        <div className='grid grid-cols-6 gap-5'>
          <HookInput customClassName='col-span-3' label='Project Name' name='name' type='text' />
          <HookReactSingleSelect
            id='projectLeadId'
            componentClassName='col-span-3'
            options={authorizedProjectLeadUsers}
            placeholder='Select Project Lead'
            label='Project Lead'
            name='projectLeadId'
          />
          {/* <HookInput
            customClassName="col-span-6"
            type="text"
            label="Address"
            name="address"
          /> */}

          <div ref={ref} className='col-span-6'>
            <Input
              type='text'
              label='Address'
              value={value}
              name='address'
              error={errors?.address?.message}
              disabled={!ready}
              changed={(e) => {
                setValue(e.target.value);
                setFormValue('address', e.target.value);
              }}
            />
            {status === 'OK' && (
              <ul className='border border-t-0 py-2 flex flex-col gap-2'>
                {data.map((suggestion) => (
                  <li
                    key={suggestion.place_id}
                    className='px-3 py-2 hover:bg-neutralColor-lightest cursor-pointer'
                    onClick={() => handleAddressSelection(suggestion)}
                  >
                    {suggestion.description}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <HookInput customClassName='col-span-2' type='text' label='City' name='city' />
          <HookReactSingleSelect
            id='state'
            componentClassName='col-span-2'
            options={States}
            getOptionLabel={(option) => (option as State).name}
            getOptionValue={(option) => (option as State).abbreviation}
            placeholder='Select State'
            name='state'
            label='State'
          />
          <HookInput customClassName='col-span-2' type='number' label='Zip / Postal Code' name='zipCode' />
          {/* <HookInput
            customClassName="col-span-2"
            type="text"
            label="Neighborhood"
            name="neighborhood"
          /> */}

          <HookReactSingleSelect
            id='Neighborhood'
            componentClassName='col-span-2'
            options={neighborhoodOptions}
            placeholder='Select Neighborhood'
            name='neighborhood'
            label='Neighborhood'
            isCreatable
            isClearable
          />
          <HookReactSingleSelect
            id='projectType'
            componentClassName='col-span-2'
            options={projectType}
            placeholder='Select Project Type'
            name='projectType'
            label='Project Type'
          />
          {projectTypeWithUnits.includes(projectTypeValue.value) && (
            <HookMaskedInput customClassName='col-span-2' label='Units' name='units' maskedComponent='numeric' />
          )}
          {projectTypeWithAvgSalesPrice.includes(projectTypeValue.value) && (
            <HookMaskedInput customClassName='col-span-2' label='Average Sales Price' name='avgSalesPrice' maskedComponent='numeric' />
          )}
          <HookReactSingleSelect
            id='constructionType'
            componentClassName='col-span-2'
            options={constructionType}
            placeholder='Select Construction Type'
            name='constructionType'
            label='Construction Type'
          />

          <HookMaskedInput customClassName='col-span-2' label='Square Feet' name='squareFeet' maskedComponent='numeric' />
        </div>
      </section>
      <section className='py-5 mt-5 border-t'>
        <h2 className={`mb-0 ${styles['form-title']}`}>Social Goals</h2>
        <p className='text-neutralColor-normal'>Optional</p>
        <ProjectSocialGoalsHookFieldArray />
      </section>
      <div className='flex justify-end mt-8'>
        <Button label='Next' btnStyle='btn-primary' type='submit' />
      </div>
    </HookForm>
  );
};
